import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  CheckIcon,
  ClockIcon,
  ReceiptRefundIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface Drink {
  id: number;
  month: number;
  event_label: string;
  abrechnungsmonat?: string | null;
  amount: number;
  paid: number;
  paid_date: string | null;
  payment_method: string | null;
  token: string;
}

const PayPalIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901A.641.641 0 0 1 5.577.36h6.964c2.717 0 4.617.652 5.65 1.94 1.013 1.265 1.225 2.785.611 4.687C17.84 9.515 15.5 11.7 11.547 11.7H8.46l-.62 3.93-.764 5.708z" />
  </svg>
);

const HERO_PHOTO =
  'https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=1600&auto=format&fit=crop&q=80';

const CARD_BG = '#1c2432';
const PAYPAL_BLUE = '#0070f3';
const PAYPAL_BLUE_DARK = '#0058c4';

const MyDrinks: React.FC = () => {
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState<Drink | null>(null);
  const [selectedDrinks, setSelectedDrinks] = useState<Drink[]>([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState<'unpaid' | 'paid'>('unpaid');
  const [showPayAllModal, setShowPayAllModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const drinksRes = await apiClient.get('/api/drinks/mydrinks');
      const drinksData = drinksRes.data || [];
      const sanitizedDrinks = drinksData.map((drink: any) => ({
        ...drink,
        amount: isNaN(parseFloat(drink.amount)) ? 0 : parseFloat(drink.amount),
      }));
      setDrinks(sanitizedDrinks);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setMessage({
          type: 'error',
          text: err.message || 'Fehler beim Laden der Daten.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const calculatePayPalFees = (amount: number) => amount * 1.0249 + 0.35;

  const handlePayPalClick = (drink: Drink) => {
    setSelectedDrink(drink);
    setModalOpen(true);
  };

  const handlePayAllClick = () => {
    setSelectedDrinks(openDrinks);
    setShowPayAllModal(true);
  };

  const handlePayAllConfirm = (useSandbox: boolean = false) => {
    if (selectedDrinks.length > 0) {
      const totalAmount = selectedDrinks.reduce((sum, drink) => sum + drink.amount, 0);
      const totalWithFees = calculatePayPalFees(totalAmount).toFixed(2);
      const customTokens = selectedDrinks.map((d) => d.token).join(';');

      const returnUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-success');
      const cancelUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-cancel');

      const baseUrl = useSandbox ? 'https://www.sandbox.paypal.com' : 'https://www.paypal.com';
      const businessEmail = useSandbox
        ? 'sb-qvwew31972863@business.example.com'
        : 'markus@tietke.com';
      const paypalLink = `${baseUrl}/cgi-bin/webscr?cmd=_xclick&business=${businessEmail}&item_name=HSV-Adlershof-Verzehrkosten&amount=${totalWithFees}&currency_code=EUR&no_shipping=1&bn=PP-FamilyFriends&custom=${customTokens}&return=${returnUrl}&cancel_return=${cancelUrl}`;
      window.open(paypalLink, '_blank');

      setMessage({
        type: 'info',
        text: useSandbox
          ? 'PayPal-Sandbox-Zahlung wurde in einem neuen Fenster geöffnet (Testmodus).'
          : 'PayPal-Zahlung wurde in einem neuen Fenster geöffnet. Bitte schließen Sie die Zahlung dort ab.',
      });

      setTimeout(() => fetchData(), 5000);
    }
    setShowPayAllModal(false);
    setSelectedDrinks([]);
  };

  const handlePayPalConfirm = (useSandbox: boolean = false) => {
    if (selectedDrink) {
      const totalWithFees = calculatePayPalFees(selectedDrink.amount).toFixed(2);
      const returnUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-success');
      const cancelUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-cancel');
      const baseUrl = useSandbox ? 'https://www.sandbox.paypal.com' : 'https://www.paypal.com';
      const businessEmail = useSandbox
        ? 'sb-qvwew31972863@business.example.com'
        : 'markus@tietke.com';
      const paypalLink = `${baseUrl}/cgi-bin/webscr?cmd=_xclick&business=${businessEmail}&item_name=HSV-Adlershof-${selectedDrink.event_label}&amount=${totalWithFees}&currency_code=EUR&no_shipping=1&bn=PP-FamilyFriends&custom=${selectedDrink.token}&return=${returnUrl}&cancel_return=${cancelUrl}`;
      window.open(paypalLink, '_blank');

      setMessage({
        type: 'info',
        text: useSandbox
          ? 'PayPal-Sandbox-Zahlung wurde in einem neuen Fenster geöffnet (Testmodus).'
          : 'PayPal-Zahlung wurde in einem neuen Fenster geöffnet. Bitte schließen Sie die Zahlung dort ab.',
      });

      setTimeout(() => fetchData(), 5000);
    }
    setModalOpen(false);
    setSelectedDrink(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDrink(null);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const getPaymentMethodLabel = (method: string | null) => {
    switch (method) {
      case 'cash':
      case 'bar':
        return 'Bar';
      case 'paypal':
        return 'PayPal';
      case 'bank_transfer':
        return 'Überweisung';
      default:
        return method || 'Unbekannt';
    }
  };

  const getEventYear = (drink: Drink) => {
    if (!drink.abrechnungsmonat) return '';
    return new Date(drink.abrechnungsmonat).getFullYear();
  };

  const getEventDisplayName = (drink: Drink) => {
    const year = getEventYear(drink);
    return year ? `${drink.event_label} ${year}` : drink.event_label;
  };

  const getMonthShort = (drink: Drink) => {
    if (drink.abrechnungsmonat) {
      try {
        const d = new Date(drink.abrechnungsmonat);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
        }
      } catch {}
    }
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    const m = Number(drink.month);
    if (!isNaN(m) && m >= 1 && m <= 12) return months[m - 1];
    return '—';
  };

  const openDrinks = useMemo(
    () =>
      drinks
        .filter((d) => d.paid === 0)
        .sort((a, b) => Number(b.month) - Number(a.month)),
    [drinks]
  );

  const paidDrinks = useMemo(
    () =>
      drinks
        .filter((d) => d.paid === 1)
        .sort((a, b) => {
          if (!a.paid_date || !b.paid_date) return 0;
          return new Date(b.paid_date).getTime() - new Date(a.paid_date).getTime();
        }),
    [drinks]
  );

  const totalOpen = openDrinks.reduce((sum, d) => sum + d.amount, 0);
  const totalPaid = paidDrinks.reduce((sum, d) => sum + d.amount, 0);
  const totalAll = totalOpen + totalPaid;
  const paidQuotePct =
    drinks.length === 0 ? 0 : Math.round((paidDrinks.length / drinks.length) * 100);
  const ringDeg = Math.max(0, Math.min(360, (paidDrinks.length / Math.max(drinks.length, 1)) * 360));

  const feesIncluded = totalOpen > 0 ? calculatePayPalFees(totalOpen) - totalOpen : 0;
  const totalIncl = totalOpen > 0 ? calculatePayPalFees(totalOpen) : 0;
  const singleFeesIfSeparate = openDrinks.length * 0.35 + totalOpen * 0.0249;
  const savings = openDrinks.length > 1 ? Math.max(singleFeesIfSeparate - feesIncluded, 0) : 0;

  const lastPaidDate =
    paidDrinks.length > 0 && paidDrinks[0].paid_date
      ? new Date(paidDrinks[0].paid_date).toLocaleDateString('de-DE', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : null;

  // Saison-Verlauf: alle Monate aus drinks gruppieren
  const monthlyTimeline = useMemo(() => {
    const map = new Map<string, { label: string; year: string; amount: number; paid: boolean }>();
    drinks.forEach((d) => {
      const key = d.abrechnungsmonat ? d.abrechnungsmonat : `m${d.month}`;
      const label = getMonthShort(d);
      const year = getEventYear(d).toString();
      const existing = map.get(key);
      if (existing) {
        existing.amount += d.amount;
        existing.paid = existing.paid && d.paid === 1;
      } else {
        map.set(key, { label, year, amount: d.amount, paid: d.paid === 1 });
      }
    });
    return Array.from(map.values()).slice(0, 7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drinks]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6">
          <div
            className="rounded-3xl p-8 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700/60 rounded mb-3" />
            <div className="h-4 w-72 bg-gray-200 dark:bg-slate-700/40 rounded" />
          </div>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-2xl p-5 animate-pulse bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div className="h-5 w-2/3 bg-gray-200 dark:bg-slate-700/60 rounded mb-2" />
              <div className="h-4 w-1/3 bg-gray-200 dark:bg-slate-700/40 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen font-sans text-gray-900 dark:text-slate-200"
      style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">
        {/* Message */}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-2xl flex items-center gap-3 backdrop-blur ${
              message.type === 'success'
                ? 'bg-emerald-500/10 text-emerald-200 border border-emerald-500/30'
                : message.type === 'error'
                ? 'bg-rose-500/10 text-rose-200 border border-rose-500/30'
                : 'bg-blue-500/10 text-blue-200 border border-blue-500/30'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
            )}
            <span className="flex-1 text-sm font-medium">{message.text}</span>
            <button
              onClick={() => setMessage({ type: '', text: '' })}
              className="hover:opacity-70"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* INTRO: Kicker + Story + Stats */}
        {drinks.length > 0 && (
          <section className="pt-2 pb-6">
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-600/80 dark:text-primary-400/80 font-semibold mb-3">
                  {openDrinks.length > 0
                    ? `${openDrinks.length} ${openDrinks.length === 1 ? 'offene Position' : 'offene Positionen'}`
                    : `${paidDrinks.length} bezahlte ${paidDrinks.length === 1 ? 'Position' : 'Positionen'}`}{' '}
                  · Stand{' '}
                  {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight mb-4">
                  Dein <span className="text-primary-600 dark:text-primary-400">Theken-Konto.</span>
                </h1>
                <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                  {totalOpen > 0 ? (
                    <>
                      Du hast{' '}
                      <span className="font-bold text-amber-700 dark:text-amber-300">
                        {formatCurrency(totalOpen)}
                      </span>{' '}
                      offen aus{' '}
                      <span className="font-bold text-gray-900 dark:text-white">
                        {openDrinks.length} {openDrinks.length === 1 ? 'Abend' : 'Abenden'}
                      </span>
                      {savings > 0 ? (
                        <>
                          . Mit der Sammelzahlung sparst du{' '}
                          <span className="font-bold text-primary-700 dark:text-primary-300">
                            {formatCurrency(savings)}
                          </span>{' '}
                          PayPal-Gebühren
                        </>
                      ) : (
                        <>. Ein Klick — und die Theke ist wieder ausgeglichen</>
                      )}
                      {lastPaidDate && (
                        <>
                          . Letzter Ausgleich:{' '}
                          <span className="font-bold text-gray-900 dark:text-white">{lastPaidDate}</span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      Saubere Sache —{' '}
                      <span className="font-bold text-primary-700 dark:text-primary-300">alles bezahlt</span>. Du hast{' '}
                      {paidDrinks.length} {paidDrinks.length === 1 ? 'Abend' : 'Abende'} (
                      <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(totalPaid)}</span>)
                      ausgeglichen
                      {lastPaidDate && (
                        <>
                          , zuletzt am{' '}
                          <span className="font-bold text-gray-900 dark:text-white">{lastPaidDate}</span>
                        </>
                      )}
                    </>
                  )}
                  .
                </p>
          </section>
        )}

        {/* HERO: Offener Betrag */}
        {totalOpen > 0 && (
          <section className="pb-6">
            <article
              className="group relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow:
                  '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              {/* Top meta pills (overlay on photo) */}
              <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 sm:px-7 pt-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-500/40 bg-amber-500/15 text-amber-200 backdrop-blur">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    Offener Betrag
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 bg-slate-950/55 text-slate-200 backdrop-blur">
                    {openDrinks.length} {openDrinks.length === 1 ? 'Position' : 'Positionen'}
                  </span>
                  {openDrinks.length > 0 && (
                    <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 bg-slate-950/55 text-slate-200 backdrop-blur">
                      {getMonthShort(openDrinks[openDrinks.length - 1])}
                      {openDrinks.length > 1 && ` – ${getMonthShort(openDrinks[0])}`}
                    </span>
                  )}
                </div>
                <div className="hidden sm:flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-slate-300/90 font-bold">
                      Bezahlt
                    </div>
                    <div className="text-sm font-black text-white tabular-nums">
                      {paidDrinks.length}{' '}
                      <span className="text-slate-400 font-bold">/ {drinks.length}</span>
                    </div>
                  </div>
                  <div className="w-px h-8 bg-white/15" />
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-primary-300 font-bold">
                      Quote
                    </div>
                    <div className="text-sm font-black text-primary-300 tabular-nums">
                      {paidQuotePct}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Hero photo with title overlay */}
              <div className="relative w-full overflow-hidden aspect-[16/10] md:aspect-[16/6]">
                <img
                  src={HERO_PHOTO}
                  alt="Theke"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  style={{ objectPosition: 'center 45%' }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(2,6,23,0.05) 0%, rgba(2,6,23,0) 30%, rgba(2,6,23,0.55) 65%, rgba(28,36,50,0.98) 100%), radial-gradient(ellipse at top right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 60%), radial-gradient(ellipse at 30% 60%, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0) 55%)',
                  }}
                />

                {/* Title block — overlaid at bottom of photo */}
                <div className="absolute inset-x-0 bottom-0 px-5 sm:px-8 pb-6 sm:pb-7 z-10">
                  <div className="flex items-end justify-between gap-4 sm:gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/20 bg-white/10 text-white backdrop-blur">
                          {openDrinks.length > 1 ? 'Sammelzahlung jetzt fällig' : 'Eine Position fällig'}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <h3
                          className="text-white font-black leading-none tabular-nums"
                          style={{
                            fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
                            letterSpacing: '-0.04em',
                            textShadow:
                              '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                          }}
                        >
                          {totalOpen.toFixed(2).replace('.', ',')}
                        </h3>
                        <span
                          className="text-amber-300 font-black"
                          style={{
                            fontSize: 'clamp(1.25rem, 3vw, 1.875rem)',
                            textShadow: '0 1px 2px rgba(0,0,0,0.55)',
                          }}
                        >
                          €
                        </span>
                      </div>
                      <p
                        className="text-white/95 text-sm md:text-lg leading-relaxed mt-3 max-w-xl"
                        style={{
                          textShadow:
                            '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)',
                        }}
                      >
                        {savings > 0 ? (
                          <>
                            Du sparst{' '}
                            <span className="font-bold text-primary-300">
                              {formatCurrency(savings)}
                            </span>{' '}
                            Gebühren, wenn du alles in einem Schwung begleichst.
                          </>
                        ) : (
                          <>Ein Klick, eine Zahlung — danach ist die Theke wieder ausgeglichen.</>
                        )}
                      </p>
                    </div>

                    {/* Paid ring overlaid on photo */}
                    <div className="hidden md:block shrink-0">
                      <div
                        className="relative w-28 h-28 rounded-full"
                        style={{
                          background: `conic-gradient(from 0deg, #34d399 0deg ${ringDeg}deg, #f59e0b ${ringDeg}deg 360deg)`,
                          padding: 5,
                        }}
                      >
                        <div
                          className="w-full h-full rounded-full flex flex-col items-center justify-center"
                          style={{ background: CARD_BG, border: '1px solid #1e293b' }}
                        >
                          <div className="text-[9px] uppercase tracking-widest text-slate-400">
                            Quote
                          </div>
                          <div className="text-3xl font-black text-white leading-none">
                            {paidQuotePct}
                          </div>
                          <div className="text-[10px] text-primary-400 font-semibold mt-0.5">
                            % bezahlt
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer breakdown — Mobile */}
              <div
                className="md:hidden relative px-5 py-5 border-t border-gray-200 dark:border-slate-800"
              >
                <div className="flex items-end justify-between gap-3 mb-1">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Gesamt inkl. Gebühr
                    </div>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="text-3xl font-black text-amber-700 dark:text-amber-300 tabular-nums leading-none">
                        {totalIncl.toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-sm text-amber-600 dark:text-amber-400 font-bold">€</span>
                    </div>
                  </div>
                  {lastPaidDate && (
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Letzter Ausgleich
                      </div>
                      <div className="text-xs font-bold text-gray-900 dark:text-white mt-0.5">{lastPaidDate}</div>
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-gray-600 dark:text-slate-400 mb-4">
                  {totalOpen.toFixed(2).replace('.', ',')} € Grundbetrag
                  <span className="text-gray-400 dark:text-slate-600"> · </span>
                  {feesIncluded.toFixed(2).replace('.', ',')} € PayPal-Gebühr (2,49 % + 0,35 €)
                </div>
                <button
                  onClick={openDrinks.length > 1 ? handlePayAllClick : () => handlePayPalClick(openDrinks[0])}
                  className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-white font-bold text-sm tracking-wide transition"
                  style={{ background: PAYPAL_BLUE }}
                >
                  <PayPalIcon className="w-4 h-4" />
                  {openDrinks.length > 1 ? 'Sammelzahlung mit PayPal' : 'Mit PayPal bezahlen'}
                </button>
              </div>

              {/* Footer breakdown — Desktop */}
              <div
                className="hidden md:block relative px-8 py-6 border-t border-gray-200 dark:border-slate-800"
              >
                <div className="grid grid-cols-5 gap-6 items-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Grundbetrag
                    </div>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                        {totalOpen.toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-[11px] text-gray-600 dark:text-slate-400">€</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      PayPal-Gebühr
                    </div>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                        {feesIncluded.toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-[11px] text-gray-600 dark:text-slate-400">€</span>
                    </div>
                    <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">2,49 % + 0,35 €</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Gesamt
                    </div>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-2xl font-black text-amber-700 dark:text-amber-300 tabular-nums">
                        {totalIncl.toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-[11px] text-amber-600 dark:text-amber-400">€</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Letzter Ausgleich
                    </div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 leading-tight">
                      {lastPaidDate ?? '—'}
                      {paidDrinks[0]?.payment_method && (
                        <>
                          <br />
                          <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">
                            via {getPaymentMethodLabel(paidDrinks[0].payment_method)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div>
                    <button
                      onClick={openDrinks.length > 1 ? handlePayAllClick : () => handlePayPalClick(openDrinks[0])}
                      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-white font-bold text-sm tracking-wide transition"
                      style={{ background: PAYPAL_BLUE }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = PAYPAL_BLUE_DARK)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = PAYPAL_BLUE)}
                    >
                      <PayPalIcon className="w-4 h-4" />
                      {openDrinks.length > 1 ? 'Sammelzahlung' : 'PayPal'}
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Savings banner */}
            {savings > 0 && (
              <div className="mt-3 flex items-start sm:items-center gap-3 px-4 py-3 rounded-2xl border border-primary-500/20 bg-primary-500/5 text-xs">
                <CheckCircleIcon className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0 mt-0.5 sm:mt-0" />
                <span className="text-gray-700 dark:text-slate-300">
                  Einzelzahlungen würden{' '}
                  <span className="font-bold text-amber-700 dark:text-amber-300">
                    {formatCurrency(singleFeesIfSeparate)}
                  </span>{' '}
                  Gebühren kosten — die Sammelzahlung nur{' '}
                  <span className="font-bold text-primary-700 dark:text-primary-300">
                    {formatCurrency(feesIncluded)}
                  </span>
                  . Du sparst{' '}
                  <span className="font-bold text-primary-700 dark:text-primary-300">{formatCurrency(savings)}</span>.
                </span>
              </div>
            )}
          </section>
        )}

        {/* PROGRESS + SUMMARY */}
        {drinks.length > 0 && (
          <section className="py-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Saison-Verlauf */}
              <article
                className="lg:col-span-2 rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{
                  boxShadow:
                    '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)',
                }}
              >
                <div className="flex items-start justify-between mb-5 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                      Saison-Verlauf
                    </div>
                    <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                      {paidQuotePct}% bezahlt · {paidDrinks.length} von {drinks.length} Abenden
                    </h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                      {openDrinks.length === 0
                        ? 'Saison komplett ausgeglichen — sauber.'
                        : `Bleib dran — noch ${openDrinks.length} ${
                            openDrinks.length === 1 ? 'Abend' : 'Abende'
                          }.`}
                    </p>
                  </div>
                </div>

                {/* Progress bar */}
                <div
                  className="relative h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/5"
                >
                  <div
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{
                      width: `${paidQuotePct}%`,
                      background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-3 text-[11px] text-gray-500 dark:text-slate-500">
                  <span>0 €</span>
                  <span className="text-gray-700 dark:text-slate-300">
                    {formatCurrency(totalPaid)} bezahlt
                  </span>
                  <span>{formatCurrency(totalAll)}</span>
                </div>

                {/* Month timeline */}
                {monthlyTimeline.length > 0 && (
                  <div className="mt-6 grid grid-cols-4 sm:grid-cols-7 gap-2">
                    {monthlyTimeline.map((m, idx) => (
                      <div
                        key={idx}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-xl ${
                          m.paid
                            ? 'bg-primary-500/10 border border-primary-500/30'
                            : 'bg-amber-500/10 border border-amber-500/30'
                        }`}
                      >
                        <span
                          className={`text-[9px] uppercase tracking-widest font-bold ${
                            m.paid ? 'text-primary-700 dark:text-primary-300' : 'text-amber-700 dark:text-amber-300'
                          }`}
                        >
                          {m.label}
                        </span>
                        <span className="text-sm font-black text-gray-900 dark:text-white tabular-nums">
                          {m.amount.toFixed(2).replace('.', ',')}
                        </span>
                        <span className={`text-[9px] ${m.paid ? 'text-primary-600 dark:text-primary-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {m.paid ? '✓ Bezahlt' : 'Offen'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </article>

              {/* Status summary */}
              <article
                className="relative rounded-3xl p-6 overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{
                  boxShadow:
                    '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)',
                }}
              >
                <div
                  className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none blur-2xl"
                  style={{ background: 'rgba(245,158,11,0.10)' }}
                />
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                  Konto-Status
                </div>
                <h4 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                  {totalOpen > 0
                    ? openDrinks.length > 1
                      ? 'In einem Klick ausgeglichen'
                      : 'Eine Zahlung trennt dich vom Ausgleich'
                    : 'Alles ausgeglichen'}
                </h4>

                <div className="mt-5 space-y-3 relative">
                  <div className="flex items-center justify-between px-4 py-3 rounded-2xl border border-amber-500/30 bg-amber-500/10">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-500/20 border border-amber-500/40"
                      >
                        <ClockIcon className="w-4 h-4 text-amber-700 dark:text-amber-300" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                          Offen
                        </div>
                        <div className="text-base font-black text-gray-900 dark:text-white tabular-nums">
                          {formatCurrency(totalOpen)}
                        </div>
                      </div>
                    </div>
                    <div className="text-[11px] text-amber-700 dark:text-amber-300 font-bold">{openDrinks.length}</div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 rounded-2xl border border-primary-500/30 bg-primary-500/10">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center bg-primary-500/20 border border-primary-500/40"
                      >
                        <CheckIcon className="w-4 h-4 text-primary-700 dark:text-primary-300" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                          Bezahlt
                        </div>
                        <div className="text-base font-black text-gray-900 dark:text-white tabular-nums">
                          {formatCurrency(totalPaid)}
                        </div>
                      </div>
                    </div>
                    <div className="text-[11px] text-primary-700 dark:text-primary-300 font-bold">{paidDrinks.length}</div>
                  </div>
                  <div
                    className="flex items-center justify-between px-4 py-3 rounded-2xl bg-gray-100 dark:bg-white/[0.02] border border-gray-200 dark:border-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                      >
                        <ReceiptRefundIcon className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">
                          Letzter Ausgleich
                        </div>
                        <div className="text-sm font-bold text-gray-900 dark:text-white">{lastPaidDate ?? '—'}</div>
                      </div>
                    </div>
                    {paidDrinks[0]?.payment_method && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-slate-950/55 text-gray-700 dark:text-slate-300 backdrop-blur">
                        {getPaymentMethodLabel(paidDrinks[0].payment_method)}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            </div>
          </section>
        )}

        {/* TABS */}
        {drinks.length > 0 && (
          <section className="py-4">
            <div className="flex items-center justify-between gap-4">
              <div
                className="inline-flex p-1 rounded-2xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <button
                  onClick={() => setActiveTab('unpaid')}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                    activeTab === 'unpaid'
                      ? 'bg-amber-500 text-slate-950'
                      : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Unbezahlt
                  {openDrinks.length > 0 && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                        activeTab === 'unpaid'
                          ? 'bg-slate-950/30 text-slate-950'
                          : 'bg-white/10 text-white'
                      }`}
                    >
                      {openDrinks.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('paid')}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                    activeTab === 'paid'
                      ? 'bg-primary-500 text-slate-950'
                      : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Bezahlt
                  {paidDrinks.length > 0 && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                        activeTab === 'paid'
                          ? 'bg-slate-950/30 text-slate-950'
                          : 'bg-white/10 text-white'
                      }`}
                    >
                      {paidDrinks.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* GLOBAL EMPTY STATE */}
        {drinks.length === 0 && (
          <section className="py-10">
            <div
              className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div
                className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                <ShoppingCartIcon className="h-10 w-10 text-gray-600 dark:text-slate-300" />
              </div>
              <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                Noch keine Verzehrkosten
              </h4>
              <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                Sobald du bei einem Vereinsabend einkehrst, taucht alles hier auf.
              </p>
            </div>
          </section>
        )}

        {/* UNPAID LIST */}
        {activeTab === 'unpaid' && openDrinks.length > 0 && (
          <section className="pb-6">
            <div
              className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              {/* List header */}
              <div
                className="px-5 sm:px-6 py-4 flex items-center gap-4 border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-white/[0.015]"
              >
                <div className="flex-1">
                  <div className="text-base sm:text-lg font-black text-gray-900 dark:text-white">
                    Offene Positionen
                  </div>
                  <div className="text-xs text-gray-600 dark:text-slate-400">
                    {openDrinks.length}{' '}
                    {openDrinks.length === 1 ? 'Abend' : 'Abende'} · zusammen{' '}
                    {formatCurrency(totalOpen)}
                    {savings > 0 && ' — Sammelzahlung empfohlen'}
                  </div>
                </div>
                {openDrinks.length > 1 && (
                  <button
                    onClick={handlePayAllClick}
                    className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-bold text-xs transition"
                    style={{ background: PAYPAL_BLUE }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = PAYPAL_BLUE_DARK)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = PAYPAL_BLUE)}
                  >
                    <PayPalIcon className="w-3.5 h-3.5" />
                    Alle bezahlen
                  </button>
                )}
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-200 dark:divide-slate-800">
                {openDrinks.map((drink) => (
                  <div
                    key={drink.id}
                    onClick={() => handlePayPalClick(drink)}
                    className="px-5 sm:px-6 py-4 flex items-center gap-4 cursor-pointer transition border-b border-gray-200 dark:border-slate-800"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div
                      className="shrink-0 flex flex-col items-center justify-center"
                      style={{
                        width: 64,
                        height: 76,
                        borderRadius: 14,
                        background:
                          'linear-gradient(180deg, rgba(245,158,11,0.16) 0%, rgba(245,158,11,0.04) 100%)',
                        border: '1px solid rgba(245,158,11,0.25)',
                      }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">
                        {getMonthShort(drink)}
                      </div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white leading-none mt-0.5">
                        {drink.month || '—'}
                      </div>
                      {getEventYear(drink) && (
                        <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                          {getEventYear(drink)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                          Offen
                        </span>
                      </div>
                      <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                        {drink.event_label}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                        Buchung #{drink.id}
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-5 shrink-0">
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                          Betrag
                        </div>
                        <div className="flex items-baseline gap-1 justify-end">
                          <span className="text-lg font-black text-amber-700 dark:text-amber-300 tabular-nums">
                            {drink.amount.toFixed(2).replace('.', ',')}
                          </span>
                          <span className="text-[11px] text-gray-600 dark:text-slate-400">€</span>
                        </div>
                      </div>
                    </div>
                    <div className="md:hidden text-right shrink-0">
                      <div className="text-base font-black text-amber-700 dark:text-amber-300 tabular-nums">
                        {formatCurrency(drink.amount)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePayPalClick(drink);
                      }}
                      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition shrink-0 bg-blue-100 dark:bg-blue-500/10 border border-blue-300 dark:border-blue-500/35 text-blue-700 dark:text-blue-300"
                    >
                      PayPal
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        viewBox="0 0 24 24"
                      >
                        <path d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* List footer */}
              <div
                className="px-5 sm:px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-white/[0.015]"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                    Summe offen
                  </div>
                  <div className="text-lg font-black text-amber-700 dark:text-amber-300 tabular-nums">
                    {formatCurrency(totalOpen)}
                  </div>
                </div>
                {openDrinks.length > 1 && (
                  <button
                    onClick={handlePayAllClick}
                    className="sm:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-bold text-xs transition"
                    style={{ background: PAYPAL_BLUE }}
                  >
                    Alle bezahlen
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      viewBox="0 0 24 24"
                    >
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </section>
        )}

        {/* PAID LIST */}
        {activeTab === 'paid' && paidDrinks.length > 0 && (
          <section className="pb-6">
            <div
              className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
              }}
            >
              <div className="divide-y divide-gray-200 dark:divide-slate-800">
                {paidDrinks.map((drink) => (
                  <div
                    key={drink.id}
                    className="px-5 sm:px-6 py-4 flex items-center gap-4 transition border-b border-gray-200 dark:border-slate-800"
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div
                      className="shrink-0 flex flex-col items-center justify-center"
                      style={{
                        width: 64,
                        height: 76,
                        borderRadius: 14,
                        background:
                          'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)',
                        border: '1px solid rgba(16,185,129,0.25)',
                      }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                        {getMonthShort(drink)}
                      </div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white leading-none mt-0.5">
                        {drink.month || '—'}
                      </div>
                      {getEventYear(drink) && (
                        <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                          {getEventYear(drink)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                          <CheckIcon className="w-3 h-3" />
                          Bezahlt
                        </span>
                        {drink.payment_method && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-slate-950/55 text-gray-700 dark:text-slate-300">
                            {getPaymentMethodLabel(drink.payment_method)}
                          </span>
                        )}
                      </div>
                      <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                        {drink.event_label}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                        Bezahlt am {formatDate(drink.paid_date)}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">
                        Betrag
                      </div>
                      <div className="text-base font-black text-primary-700 dark:text-primary-300 tabular-nums">
                        {formatCurrency(drink.amount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                className="px-5 sm:px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-white/[0.015]"
              >
                <div className="text-[11px] text-gray-500 dark:text-slate-500">
                  Gesamt bezahlt: {formatCurrency(totalPaid)}
                </div>
                {lastPaidDate && (
                  <div className="text-[11px] text-gray-500 dark:text-slate-500">
                    Letzter Ausgleich: {lastPaidDate}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* TAB EMPTY STATES */}
        {activeTab === 'unpaid' && openDrinks.length === 0 && drinks.length > 0 && (
          <section className="pb-6">
            <div
              className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div
                className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-primary-500/10 border border-primary-500/30"
              >
                <CheckCircleIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="text-lg font-black text-gray-900 dark:text-white">Alles bezahlt!</h4>
              <p className="text-gray-600 dark:text-slate-400 text-sm mt-2 max-w-md mx-auto">
                Keine offenen Positionen. Cheers!
              </p>
            </div>
          </section>
        )}

        {activeTab === 'paid' && paidDrinks.length === 0 && drinks.length > 0 && (
          <section className="pb-6">
            <div
              className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div
                className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 bg-amber-500/10 border border-amber-500/30"
              >
                <ReceiptRefundIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <h4 className="text-lg font-black text-gray-900 dark:text-white">Noch keine bezahlten Abende</h4>
              <p className="text-gray-600 dark:text-slate-400 text-sm mt-2 max-w-md mx-auto">
                Beleg deinen ersten Ausgleich — die Liste wächst von selbst.
              </p>
            </div>
          </section>
        )}

        {/* PayPal Single Modal */}
        {modalOpen && selectedDrink && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={handleModalClose}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[480px] flex flex-col animate-slideUp overflow-hidden"
              style={{
                background: CARD_BG,
                border: '1px solid #334155',
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
                maxHeight: 'calc(100vh - 32px)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">
                    PayPal-Zahlung
                  </div>
                  <h2 className="text-2xl font-black text-white tracking-tight mt-1">
                    Zahlung bestätigen
                  </h2>
                  <p className="text-slate-400 text-sm mt-1">
                    {getEventDisplayName(selectedDrink)}
                  </p>
                </div>
                <button
                  onClick={handleModalClose}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
                  aria-label="Schließen"
                >
                  <XMarkIcon className="w-4 h-4 text-slate-300" />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto flex-1">
                <div
                  className="relative px-6 py-5 rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(0,112,243,0.12)', border: '1px solid rgba(0,112,243,0.35)' }}
                >
                  <div
                    className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none blur-2xl"
                    style={{ background: 'rgba(0,112,243,0.25)' }}
                  />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest text-blue-200/80 font-bold">
                      Gesamt
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span
                        className="text-5xl font-black text-white leading-none tabular-nums"
                        style={{ letterSpacing: '-0.04em' }}
                      >
                        {(selectedDrink.amount * 1.0249 + 0.35).toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-2xl font-black text-blue-200">€</span>
                    </div>
                    <div className="text-xs text-blue-200/80 mt-2">
                      inkl. {(selectedDrink.amount * 0.0249 + 0.35).toFixed(2).replace('.', ',')}{' '}
                      € PayPal-Gebühr (2,49 % + 0,35 €)
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div
                    className="px-3 py-2.5 rounded-xl flex items-center justify-between"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-slate-400">Beitrag</span>
                    <span className="text-white font-bold tabular-nums">
                      {formatCurrency(selectedDrink.amount)}
                    </span>
                  </div>
                  <div
                    className="px-3 py-2.5 rounded-xl flex items-center justify-between"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-slate-400">Gebühren</span>
                    <span className="text-white font-bold tabular-nums">
                      {formatCurrency(selectedDrink.amount * 0.0249 + 0.35)}
                    </span>
                  </div>
                </div>

                <div
                  className="mt-4 px-3 py-2.5 rounded-xl flex items-start gap-2 text-xs text-slate-300"
                  style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.20)' }}
                >
                  <ExclamationCircleIcon className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  <span>
                    Die Zahlung wird in einem neuen Tab über PayPal abgewickelt. Alternativ kannst du
                    bar beim Kassenwart bezahlen.
                  </span>
                </div>

                {openDrinks.length > 1 && (
                  <div
                    className="mt-3 px-3 py-2.5 rounded-xl flex items-start gap-2 text-xs text-slate-300"
                    style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.20)' }}
                  >
                    <CheckCircleIcon className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" />
                    <span>
                      Mit "Alle bezahlen" ({openDrinks.length} Positionen) sparst du{' '}
                      <span className="font-bold text-primary-300">
                        {formatCurrency(savings)}
                      </span>{' '}
                      Gebühren.
                    </span>
                  </div>
                )}
              </div>

              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={handleModalClose}
                  className="px-4 py-3 rounded-xl text-slate-200 text-sm font-semibold transition"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handlePayPalConfirm(false)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold text-sm transition"
                  style={{ background: PAYPAL_BLUE }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = PAYPAL_BLUE_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = PAYPAL_BLUE)}
                >
                  <PayPalIcon className="w-4 h-4" />
                  Mit PayPal bezahlen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PayPal Pay-All Modal */}
        {showPayAllModal && selectedDrinks.length > 0 && (
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => {
              setShowPayAllModal(false);
              setSelectedDrinks([]);
            }}
          >
            <div
              className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-[600px] flex flex-col animate-slideUp overflow-hidden"
              style={{
                background: CARD_BG,
                border: '1px solid #334155',
                boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)',
                maxHeight: 'calc(100vh - 32px)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">
                    PayPal · Sammelzahlung
                  </div>
                  <h2 className="text-2xl font-black text-white tracking-tight mt-1">
                    Zahlung bestätigen
                  </h2>
                  <p className="text-slate-400 text-sm mt-1">
                    {selectedDrinks.length} Positionen zusammengefasst
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPayAllModal(false);
                    setSelectedDrinks([]);
                  }}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
                  aria-label="Schließen"
                >
                  <XMarkIcon className="w-4 h-4 text-slate-300" />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto flex-1">
                <div
                  className="relative px-6 py-5 rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(0,112,243,0.12)', border: '1px solid rgba(0,112,243,0.35)' }}
                >
                  <div
                    className="absolute -top-8 -right-8 w-32 h-32 rounded-full pointer-events-none blur-2xl"
                    style={{ background: 'rgba(0,112,243,0.25)' }}
                  />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest text-blue-200/80 font-bold">
                      Gesamt
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span
                        className="text-5xl font-black text-white leading-none tabular-nums"
                        style={{ letterSpacing: '-0.04em' }}
                      >
                        {calculatePayPalFees(
                          selectedDrinks.reduce((sum, d) => sum + d.amount, 0)
                        )
                          .toFixed(2)
                          .replace('.', ',')}
                      </span>
                      <span className="text-2xl font-black text-blue-200">€</span>
                    </div>
                    <div className="text-xs text-blue-200/80 mt-2">
                      inkl.{' '}
                      {(
                        calculatePayPalFees(selectedDrinks.reduce((sum, d) => sum + d.amount, 0)) -
                        selectedDrinks.reduce((sum, d) => sum + d.amount, 0)
                      )
                        .toFixed(2)
                        .replace('.', ',')}{' '}
                      € PayPal-Gebühr (2,49 % + 0,35 €)
                    </div>
                  </div>
                </div>

                {/* Positions list */}
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                    Enthaltene Positionen
                  </div>
                  <div
                    className="rounded-xl divide-y max-h-44 overflow-y-auto"
                    style={{ borderColor: '#1e293b', border: '1px solid #1e293b' }}
                  >
                    {selectedDrinks.map((drink) => (
                      <div
                        key={drink.id}
                        className="px-3 py-2.5 flex items-center justify-between text-sm"
                        style={{ borderBottom: '1px solid #1e293b' }}
                      >
                        <div className="text-slate-200 truncate pr-3">
                          {getEventDisplayName(drink)}
                        </div>
                        <div className="text-white font-bold tabular-nums shrink-0">
                          {formatCurrency(drink.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div
                    className="px-3 py-2.5 rounded-xl flex items-center justify-between"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-slate-400">Grundbetrag</span>
                    <span className="text-white font-bold tabular-nums">
                      {formatCurrency(selectedDrinks.reduce((sum, d) => sum + d.amount, 0))}
                    </span>
                  </div>
                  <div
                    className="px-3 py-2.5 rounded-xl flex items-center justify-between"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <span className="text-slate-400">Gebühren</span>
                    <span className="text-white font-bold tabular-nums">
                      {formatCurrency(
                        calculatePayPalFees(selectedDrinks.reduce((sum, d) => sum + d.amount, 0)) -
                          selectedDrinks.reduce((sum, d) => sum + d.amount, 0)
                      )}
                    </span>
                  </div>
                </div>

                {savings > 0 && (
                  <div
                    className="mt-4 px-3 py-2.5 rounded-xl flex items-start gap-2 text-xs text-slate-300"
                    style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.20)' }}
                  >
                    <CheckCircleIcon className="w-4 h-4 text-primary-400 shrink-0 mt-0.5" />
                    <span>
                      Du sparst{' '}
                      <span className="font-bold text-primary-300">
                        {formatCurrency(savings)}
                      </span>{' '}
                      Gebühren gegenüber {selectedDrinks.length} Einzelzahlungen.
                    </span>
                  </div>
                )}

                <div
                  className="mt-3 px-3 py-2.5 rounded-xl flex items-start gap-2 text-xs text-slate-300"
                  style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.20)' }}
                >
                  <ExclamationCircleIcon className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  <span>
                    Alle {selectedDrinks.length} Positionen werden in einer Zahlung zusammengefasst
                    und in einem neuen Tab über PayPal abgewickelt.
                  </span>
                </div>
              </div>

              <div
                className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
              >
                <button
                  onClick={() => {
                    setShowPayAllModal(false);
                    setSelectedDrinks([]);
                  }}
                  className="px-4 py-3 rounded-xl text-slate-200 text-sm font-semibold transition"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => handlePayAllConfirm(false)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-bold text-sm transition"
                  style={{ background: PAYPAL_BLUE }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = PAYPAL_BLUE_DARK)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = PAYPAL_BLUE)}
                >
                  <PayPalIcon className="w-4 h-4" />
                  Mit PayPal bezahlen
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-slideUp { animation: slideUp 0.2s ease; }
        `}</style>
      </div>
    </div>
  );
};

export default MyDrinks;
